/**
 * Pure helpers for the wizard's `radio-cards` fields and for the
 * `visible_when` expressions that gate conditional fields.
 *
 * No `@/` imports — this module is unit-tested directly under node
 * (`node --test tests/unit/wizard-field-cards.test.mjs`).
 *
 * Author: Matew Makhatadze
 */

/**
 * The icon vocabulary the catalog templates actually use, mapped onto the
 * Font Awesome classes this app already ships. Kept as a closed map rather
 * than an interpolated `fa-${icon}` so an unknown name degrades to a neutral
 * glyph instead of rendering an invisible empty box.
 *
 * Operator-side templates spell the same icons with a Bootstrap `bi-` prefix
 * (`bi-server`), so the prefix is stripped before the lookup.
 */
export const CARD_ICONS = {
  server: 'fas fa-server',
  layers: 'fas fa-layer-group',
  monitor: 'fas fa-desktop',
  git: 'fab fa-gitlab',
  gitlab: 'fab fa-gitlab',
  database: 'fas fa-database',
  'hdd-stack': 'fas fa-hard-drive',
  hdd: 'fas fa-hard-drive',
  brain: 'fas fa-brain',
  zap: 'fas fa-bolt',
  bolt: 'fas fa-bolt',
  archive: 'fas fa-box-archive',
  code: 'fas fa-code',
  cube: 'fas fa-cube'
}

export const CARD_ICON_FALLBACK = 'fas fa-cube'

/** `'bi-server'` / `' Server '` → `'fas fa-server'`; anything unknown → the fallback. */
export function cardIconClass(icon) {
  const key = String(icon || '')
    .trim()
    .toLowerCase()
    .replace(/^(bi|fa|fas|fab)-/, '')
  return CARD_ICONS[key] || CARD_ICON_FALLBACK
}

/**
 * Normalise a field's `options[]` into card rows. Accepts the object form the
 * catalog uses (`{value, label, description, icon, version}`) and the bare
 * scalar form (`['a', 'b']`), so a card grid never depends on the richer shape
 * being present.
 *
 * A version is read from `version` or `current_version` — the renderer shows a
 * badge only when the option carries one, so no badge is invented.
 */
export function cardOptionsFrom(field) {
  const raw = (field && field.options) || []
  const out = []
  for (const o of raw) {
    if (o && typeof o === 'object') {
      if (o.value === undefined || o.value === null) continue
      const version = o.version ?? o.current_version
      out.push({
        value: o.value,
        label: o.label ?? String(o.value),
        description: o.description || '',
        version: version ? String(version) : '',
        iconClass: cardIconClass(o.icon)
      })
    } else if (o !== undefined && o !== null && o !== '') {
      out.push({
        value: o,
        label: String(o),
        description: '',
        version: '',
        iconClass: CARD_ICON_FALLBACK
      })
    }
  }
  return out
}

/**
 * Should a field declaring `type: 'radio-cards'` render as cards?
 *
 * Only when it has usable options. Without them the caller keeps the earlier
 * behaviour (a read-only display of the held value), which is deliberately NOT
 * regressed here: an empty card grid is as broken as an empty dropdown.
 */
export function wantsCards(field) {
  const t = String((field && field.type) || '').toLowerCase()
  return t === 'radio-cards' && cardOptionsFrom(field).length > 0
}

/**
 * Is this field the form's *lead* choice — the unconditional, required
 * card choice whose value gates the fields below it?
 *
 * Role, not name: a required `radio-cards` field with usable options and no
 * `visible_when` of its own. `cs-application`'s `template_type` qualifies;
 * `database_type` (gated behind `enable_database`) does not, and neither does
 * an optional type picker.
 */
export function isLeadChoiceField(field) {
  if (!field || typeof field !== 'object') return false
  if (field.visible_when) return false
  if (field.required !== true) return false
  return wantsCards(field)
}

const VISIBLE_WHEN_RE = /^\s*([\w.]+)\s*(===|!==|==|!=)\s*'?([^']*?)'?\s*$/

/**
 * Evaluate one `visible_when` expression against the current form values.
 *
 * The catalog writes both JS-ish spellings — `template_type != 'gitlab-import'`
 * and `enable_database === true`. The earlier two-character operator pattern
 * matched the first `==` of a `===` and then captured `"= true"` as the target,
 * so EVERY strict-equality expression compared against the literal `"= true"`
 * and its field stayed hidden no matter what the toggle said. For
 * `cs-application` that silently hid ~70 of 93 fields: enabling Database,
 * Redis, RabbitMQ, autoscaling, health checks, scheduling or device mounts
 * revealed nothing at all.
 *
 * Comparison stays string-coerced (the values object holds real booleans while
 * the schema writes a bare `true`), and an expression this parser does not
 * understand is treated as visible — never hidden on a guess.
 */
export function evalVisibleWhen(expr, values) {
  if (!expr) return true
  const m = String(expr).match(VISIBLE_WHEN_RE)
  if (!m) return true
  const [, name, op, raw] = m
  const bag = values && typeof values === 'object' ? values : {}
  const current = String(bag[name] ?? '')
  const target = String(raw)
  const equal = current === target
  return op === '==' || op === '===' ? equal : !equal
}

/**
 * Fields whose answer is a few characters — a port, a count, a resource
 * quantity, a yes/no — are laid out two to a row on the Configure step so a
 * long template does not become a long scroll. The decision lives here, not in
 * the renderer, because it is a layout fact the wizard owns (the renderer only
 * knows one field at a time).
 */
const COMPACT_TYPES = new Set(['number', 'integer', 'boolean', 'switch', 'checkbox', 'bool', 'toggle'])
const COMPACT_NAMES = /^(cpu|memory)_(request|limit)$/

export function isCompactField(field) {
  if (!field || typeof field !== 'object') return false
  const type = String(field.type || '').toLowerCase()
  const ui = String(field.ui || '').toLowerCase()
  if (ui) return false // bespoke editors (branches, domain cards, pickers) take the full row
  if (COMPACT_TYPES.has(type)) return true
  return COMPACT_NAMES.test(String(field.name || ''))
}

/**
 * Fields the platform sets for the customer and the wizard therefore never
 * asks about — on any template that declares them (cs-application and the
 * catalog apps that share its blocks).
 *
 *   - `organization` — the GitLab apps group. The server always uses the
 *     caller's ACTIVE organisation's group, so switching organisation needs
 *     nothing from the client and no value is sent.
 *   - `container_image`, `image_pull_secret` — built and wired server-side.
 *   - node scheduling, device mounts and the security context — cluster
 *     policy, not an application choice.
 *
 * A field object carrying `platform_managed: true` is treated the same way, so
 * cluster-api can mark further fields without a website release.
 *
 * Hidden for CUSTOMERS only. A platform operator (super admin or platform
 * admin — the same rule as NavBar's `isOperator`) still sees and sets every
 * one of these, and their values are sent as entered.
 */
export const PLATFORM_MANAGED_FIELDS = Object.freeze([
  'organization',
  'container_image',
  'image_pull_secret',
  'enable_scheduling',
  '_subsection_node_selectors',
  'node_selectors',
  '_subsection_tolerations',
  'tolerations',
  'enable_device_mounts',
  'device_mounts',
  'privileged_mode',
  '_subsection_security_context',
  'enable_security_context',
  'run_as_user',
  'run_as_group'
])

const PLATFORM_MANAGED_SET = new Set(PLATFORM_MANAGED_FIELDS)

/** Super admin or platform admin — the operator rule NavBar's `isOperator` uses. */
export function isPlatformOperatorUser(user) {
  return !!user && typeof user === 'object' && (user.is_super_admin === true || user.is_admin === true)
}

/**
 * Is this field (object or bare name) one the platform sets itself, and so
 * hidden from this viewer? Always false for an operator.
 */
export function isPlatformManagedField(field, { operator = false } = {}) {
  if (operator === true) return false
  if (typeof field === 'string') return PLATFORM_MANAGED_SET.has(field)
  if (!field || typeof field !== 'object') return false
  if (field.platform_managed === true) return true
  return PLATFORM_MANAGED_SET.has(String(field.name || ''))
}

/**
 * Every managed field name for a template: the fixed list plus whatever the
 * template flags `platform_managed: true` (top-level or per component).
 */
export function platformManagedNames(template) {
  const names = new Set(PLATFORM_MANAGED_FIELDS)
  const take = (fields) => {
    ;(Array.isArray(fields) ? fields : []).forEach((f) => {
      if (f && f.name && f.platform_managed === true) names.add(String(f.name))
    })
  }
  if (template && typeof template === 'object') {
    take(template.fields)
    take(template.template_fields)
    ;(Array.isArray(template.components) ? template.components : []).forEach((c) => take(c && c.fields))
  }
  return names
}

/** A copy of `values` without the platform-managed keys (kept whole for an operator). */
export function stripPlatformManaged(values, template, { operator = false } = {}) {
  const bag = values && typeof values === 'object' ? values : {}
  if (operator === true) return { ...bag }
  const names = platformManagedNames(template)
  const out = {}
  Object.keys(values && typeof values === 'object' ? values : {}).forEach((k) => {
    if (!names.has(k)) out[k] = values[k]
  })
  return out
}
